import { CircularProgress } from '@material-ui/core';
import { useCallback, useEffect, useState, useRef } from 'react';

const MSG = {
    INIT: 'SEATING_INIT',
    SEAT_SELECTED: 'SEAT_SELECTED',
    SEAT_DESELECTED: 'SEAT_DESELECTED',
    SEATS_UPDATED: 'SEATS_UPDATED',
    SEATS_CONFIRMED: 'SEATS_CONFIRMED',
    READY: 'IFRAME_READY'
};

function buildIframeSrc(baseSrc, { showId, timeSlotId, tickets }) {
    // {widgetUrl}/seat-picker-iframe?showId={showId}&timeSlotId={timeslotId}&tickets={JSON}
    const url = new URL(`${baseSrc}/seat-picker-iframe`);
    url.searchParams.set('showId', showId);
    url.searchParams.set('timeSlotId', timeSlotId);
    url.searchParams.set('tickets', JSON.stringify(tickets));
    return url.toString();
}

const SeatingIframe = ({
    src,
    showId,
    timeSlotId,
    tickets = [],
    selectedSeats = [],
    onSeatSelected,
    onSeatDeselected,
    onSeatsUpdated,
    onSeatsConfirmed,
    onNoSeatingRequired,
    onReady
}) => {
    const iframeRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [noSeating, setNoSeating] = useState(false);

    const iframeOrigin = new URL(src).origin;

    const resolvedSrc = buildIframeSrc(src, { showId, timeSlotId, tickets });

    const sendToIframe = useCallback(
        message => {
            iframeRef.current?.contentWindow?.postMessage(message, iframeOrigin);
        },
        [iframeOrigin]
    );

    const handleIframeReady = useCallback(() => {
        sendToIframe({
            type: MSG.INIT,
            showId,
            timeSlotId,
            tickets,
            selectedSeats
        });
        setIsLoaded(true);
        onReady?.();
    }, [sendToIframe, showId, timeSlotId, tickets, selectedSeats, onReady]);

    useEffect(() => {
        const handleMessage = event => {
            if (event.origin !== iframeOrigin) {
                return;
            }
            const { type, seat, seats, confirmed } = event.data ?? {};
            switch (type) {
                case MSG.READY:
                    handleIframeReady();
                    break;
                case MSG.SEAT_SELECTED:
                    onSeatSelected?.(seat);
                    break;
                case MSG.SEAT_DESELECTED:
                    onSeatDeselected?.(seat);
                    break;
                case MSG.SEATS_UPDATED:
                    onSeatsUpdated?.(seats);
                    break;
                case MSG.SEATS_CONFIRMED:
                    if (event.data.noSeatingRequired) {
                        setNoSeating(true);
                        onNoSeatingRequired?.();
                    }
                    onSeatsConfirmed?.({ confirmed, seats });
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [
        handleIframeReady,
        onSeatSelected,
        onSeatDeselected,
        onSeatsUpdated,
        onSeatsConfirmed,
        iframeOrigin,
        onNoSeatingRequired
    ]);

    const iframeHeight = noSeating ? '120px' : '500px';

    return (
        <div
            style={{
                width: '100%',
                height: isLoaded ? iframeHeight : '100px',
                overflow: 'hidden',
                position: 'relative'
            }}
        >
            {!isLoaded && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f5f5f5',
                        zIndex: 1
                    }}
                >
                    <CircularProgress />
                </div>
            )}
            <iframe
                ref={iframeRef}
                src={resolvedSrc}
                title="Seating Map"
                allow="fullscreen"
                style={{
                    border: 'none',
                    width: 'calc(100% + 17px)',
                    height: `calc(${iframeHeight} + 17px)`,
                    marginBottom: '-17px',
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'scale(1)' : 'scale(0.98)',
                    transition: 'opacity 0.3s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transformOrigin: 'top center'
                }}
            />
        </div>
    );
};

export default SeatingIframe;
